/**
 * RED-262: overlay ML-provided condition values onto comparable rows.
 *
 * The persisted prediction's `similar_properties[].pivot_values` carries pivot
 * codes for the five Kohandustabel condition families, decoded server-side
 * from the comparable's serving row (observed raw data first, imputed
 * `_filled` / hooneosa `_display` twins second — see ComparableFieldValueMapper).
 *
 * Overlay rules:
 * - only fills a field whose DB-sourced value is empty — a locally enriched
 *   pivot value always wins over anything the ML says;
 * - `imputed: true` fields are recorded in `row.imputed_fields` so the table
 *   renders the AI badge; observed API values fill silently (no badge);
 * - display-layer only: nothing here persists — the value reaches the pivot
 *   tables only if the user actively edits the cell (which clears the flag in
 *   updateComparisonTransactionField).
 */

export const IMPUTABLE_PIVOT_FIELDS = [
  'ehitise_seisukord',
  'hooneosa_seisukord',
  'kinnistu_parkimine',
  'panipaik',
  'rodu',
]

const isEmptyValue = v =>
  v == null || v === '' || (Array.isArray(v) && v.length === 0)

/**
 * @param {Object} row - composed comparable row (composeData shape)
 * @param {Object|null|undefined} similarProperty - the prediction's similar_properties entry for this row
 * @returns {Object} a new row object with overlaid values + `imputed_fields` flags
 */
export function overlayImputedFields(row, similarProperty) {
  const pivotValues = similarProperty?.pivot_values
  if (!pivotValues || typeof pivotValues !== 'object') {
    return row
  }

  const out = { ...row }
  const imputedFields = { ...(row.imputed_fields || {}) }

  for (const field of IMPUTABLE_PIVOT_FIELDS) {
    const entry = pivotValues[field]
    if (!Array.isArray(entry?.values) || entry.values.length === 0) {
      continue
    }
    if (!isEmptyValue(out[field])) {
      continue
    }
    out[field] = [...entry.values]
    if (entry.imputed) {
      imputedFields[field] = true
    }
  }

  if (Object.keys(imputedFields).length > 0) {
    out.imputed_fields = imputedFields
  }

  return out
}
